import { fieldIsNotValidText, fieldIsRequiredText, invalidDateText, invalidEmailText, invalidNumberText, invalidSelectionText } from '@/features/payload-cms/components/form/static-form-texts';
import type { ExtendedFormType, FormSection } from '@/features/payload-cms/components/form/types';
import { getFormStorageKey } from '@/features/payload-cms/components/form/utils/get-form-storage-key';
import { usePostHog } from '@/providers/posthog-context';
import { type Locale, type StaticTranslationString } from '@/types/types';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import type { FieldValues, UseFormSetError } from 'react-hook-form';
interface UseFormSubmissionProperties {
  formId: string;
  config: ExtendedFormType;
  isPreviewMode?: boolean;
  locale: Locale;
  setError?: UseFormSetError<FieldValues>;
  formSections?: FormSection[];
  setCurrentStepIndex?: (index: number) => void;
  /** Called once the submission is stored, instead of a redirect. */
  onSubmitted?: (() => void) | undefined;
}
interface PreviewData {
  message: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any[];
}
interface SubmissionField {
  field: string;
  value: unknown;
}
interface UseFormSubmissionReturn {
  status: 'idle' | 'loading' | 'success' | 'error';
  errorMessage: string | undefined;
  previewData: PreviewData | undefined; // Change null to undefined
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  submit: (data: Record<string, any>) => Promise<void>;
  reset: () => void;
}
const allGoodPreviewText: StaticTranslationString = {
  en: 'All good – but this is just a preview. No data has been submitted. The following data would be submitted:',
  de: 'Alles gut - aber das ist nur eine Vorschau. Keine Daten wurden übermittelt. Folgende Daten würden übermittelt werden:',
  fr: "Tout va bien – mais ceci n'est qu'un aperçu. Aucune donnée n'a été transmise. Les données suivantes seraient transmises :"
};
const failedToSubmitText: StaticTranslationString = {
  en: 'Failed to submit form. Please try again later.',
  de: 'Formularübermittlung fehlgeschlagen. Bitte versuchen Sie es später erneut.',
  fr: "Échec de l'envoi du formulaire. Veuillez réessayer plus tard."
};
const correctHighlightedErrorsText: StaticTranslationString = {
  en: 'Please correct the highlighted errors.',
  de: 'Bitte korrigieren Sie die markierten Fehler.',
  fr: 'Veuillez corriger les erreurs mises en évidence.'
};
export const useFormSubmission = ({
  formId,
  config,
  isPreviewMode,
  locale,
  setError,
  formSections,
  setCurrentStepIndex,
  onSubmitted
}: UseFormSubmissionProperties): UseFormSubmissionReturn => {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [previewData, setPreviewData] = useState<PreviewData | undefined>();
  const router = useRouter();
  const posthog = usePostHog();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const submit = async (data: Record<string, any>): Promise<void> => {
    setStatus('loading');
    setErrorMessage(undefined);

    // 1. Format Data (Comma separated arrays)
    // Map initial data to array of objects
    const initialData: SubmissionField[] = Object.entries(data).map(([name, value]) => ({
      field: name,
      value: value as unknown
    }));

    // Clone to mutable array for processing
    let dataToSend: SubmissionField[] = [...initialData];

    // convert multi-select values to comma-separated strings

    for (let index = 0; index < dataToSend.length; index++) {
      const fieldData = dataToSend[index];
      if (!fieldData) continue;
      if (Array.isArray(fieldData.value)) {
        dataToSend[index] = {
          ...fieldData,
          value: fieldData.value.map(String).join(', ')
        };
      }
    }

    // remove all fields that have no value (but keep empty strings if intentionally set)
    dataToSend = dataToSend.filter(fieldData_0 => fieldData_0.value !== undefined);

    // 2. Handle Preview
    if (isPreviewMode === true) {
      // Simulate delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      setStatus('success');
      setPreviewData({
        message: allGoodPreviewText[locale],
        data: dataToSend
      });
      return;
    }

    // 3. API Call
    try {
      const resp = await fetch(`/api/form-submissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          form: formId,
          submissionData: dataToSend
        })
      });
      if (!resp.ok) {
        const errorData = (await resp.json()) as {
          errors?: {
            data?: {
              field: string;
              message: string;
            }[];
            message?: string;
          }[];
          message?: string;
        };

        // Handle field-level validation errors from payload
        if (errorData.errors?.[0]?.data && Array.isArray(errorData.errors[0].data)) {
          const fieldErrors = errorData.errors[0].data;
          if (setError) {
            const capturedErrors: string[] = [];
            for (const error_0 of fieldErrors) {
              let message_0 = error_0.message;
              switch (message_0) {
                case 'required':
                  {
                    message_0 = fieldIsRequiredText[locale];
                    break;
                  }
                case 'invalid_email':
                  {
                    message_0 = invalidEmailText[locale];
                    break;
                  }
                case 'invalid_number':
                  {
                    message_0 = invalidNumberText[locale];
                    break;
                  }
                case 'invalid_date':
                  {
                    message_0 = invalidDateText[locale];
                    break;
                  }
                case 'invalid_format':
                  {
                    message_0 = fieldIsNotValidText[locale];
                    break;
                  }
                case 'invalid_selection':
                  {
                    message_0 = invalidSelectionText[locale];
                    break;
                  }
              }
              capturedErrors.push(`${error_0.field}: ${error_0.message}`);
              setError(error_0.field, {
                type: 'server',
                message: message_0
              });
            }
            posthog?.capture('form_validation_failed', {
              form_id: formId,
              error_message: capturedErrors.join(', '),
              source: 'server'
            });

            // Navigate to the step containing the first errored field
            if (formSections && setCurrentStepIndex && fieldErrors.length > 0) {
              const firstError = fieldErrors[0];
              if (firstError) {
                const firstErrorField = firstError.field;
                const stepIndex = formSections.findIndex(section => section.fields.some(f => {
                  if ('name' in f && f.name === firstErrorField) return true;
                  if (f.blockType === 'conditionedBlock') {
                    return f.fields.some(sub => 'name' in sub && sub.name === firstErrorField);
                  }
                  return false;
                }));
                if (stepIndex !== -1) {
                  setCurrentStepIndex(stepIndex);
                }
              }
            }
            throw new Error(correctHighlightedErrorsText[locale]);
          }
        }
        throw new Error(errorData.errors?.[0]?.message ?? errorData.message ?? failedToSubmitText[locale]);
      }
      await resp.json();
      setStatus('success');

      // Clear session storage on success
      if (typeof formId === 'string' && formId !== '') {
        sessionStorage.removeItem(getFormStorageKey(formId, 'state'));
        sessionStorage.removeItem(getFormStorageKey(formId, 'step'));
      }
      if (onSubmitted !== undefined) {
        onSubmitted();
      } else if (config.confirmationType === 'redirect' && typeof config.redirect?.url === 'string' && config.redirect.url.length > 0) {
        router.push(config.redirect.url);
      }
    } catch (error: unknown) {
      setStatus('error');
      const message = error instanceof Error && error.message.length > 0 ? error.message : 'Submission failed';
      const isFieldValidationError = Object.values(correctHighlightedErrorsText).includes(message);
      if (isFieldValidationError) {
        // Validation errors are already captured prior to throwing this error
        setErrorMessage(message);
      } else if (message.includes('initializing Payload')) {
        posthog?.capture('form_submission_error', {
          error_type: 'payload_initialization',
          form_id: formId,
          original_message: message
        });
        setErrorMessage(failedToSubmitText[locale]);
      } else {
        posthog?.capture('form_submission_error', {
          error_type: 'submission_failed',
          form_id: formId,
          error_message: message
        });
        setErrorMessage(message);
      }
    }
  };
  const reset = (): void => {
    setStatus('idle');
    setErrorMessage(undefined);
    setPreviewData(undefined);
    // Clear session storage on reset
    if (typeof formId === 'string' && formId !== '') {
      sessionStorage.removeItem(getFormStorageKey(formId, 'state'));
      sessionStorage.removeItem(getFormStorageKey(formId, 'step'));
    }
  };
  return {
    status,
    errorMessage,
    previewData,
    submit,
    reset
  };
};